import { gql } from '@apollo/client';

/**
 * RMA requests — the first tab under Devices Fulfillment. Each row is a customer's submission of
 * one of the four HubSpot RMA forms, projected into Postgres by a five-minute poll
 * (see apps/api/src/app/order-devices/rma.service.ts).
 *
 * Operation names match their GraphQL field names so `refetchQueries: ['deviceRmaRequests']`
 * resolves.
 *
 * Note there is no create and no edit operation here. Every customer-submitted field belongs to
 * HubSpot and is refreshed by the poll; the only write on this surface is the verification stamp.
 */

const RMA_FIELDS = `
  submissionId
  rmaNumber
  hubspotTicketId
  subject
  deviceModel
  customerEmail
  orderNumber
  orderLocation
  deviceCount
  issueDescription
  proofOfPurchaseUrl
  restockingAck
  restockingInvoiceId
  restockingInvoiceUrl
  restockingFeeMinor
  restockingCurrency
  restockingInvoicedAt
  restockingInvoicedBy
  shipToName
  shipToLine1
  shipToLine2
  shipToCity
  shipToState
  shipToZip
  shipToCountry
  verifiedShipToName
  verifiedShipToLine1
  verifiedShipToLine2
  verifiedShipToCity
  verifiedShipToState
  verifiedShipToZip
  verifiedShipToCountry
  verifiedShipToAt
  verifiedShipToBy
  sourceFormId
  submittedAt
  previousOrderNumber
  verifiedDeviceCount
  verifiedOrderDate
  warrantyState
  warrantyEndsOn
  warrantyDaysOwned
  warrantyDaysRemaining
  withinReturnWindow
  troubleshootingNotes
  verifiedAt
  verifiedBy
  verified
  returnedAt
  returnedBy
  returnCondition
  returnMissingItems
  returnTestedOutcome
  returnTestNotes
  returnTestedAt
  returnTestedBy
  returnStatus
  dismissedAt
  dismissedBy
  dismissedReason
  duplicateOfRmaNumber
  dismissed
  replacementOrderId
  replacementOrderName
  replacementOrderUrl
  replacementOrderedAt
  replacementOrderedBy
  replacementOrderManual
  replacementTrackingNumber
  replacementTrackingUrl
  replacementTrackingAt
  replacementTrackingBy
  firstSeenAt
  lastSyncedAt
`;

/**
 * The ledger, newest-submitted first.
 *
 * `filter.verified` is TRI-STATE — true = verified only, false = the unverified working queue
 * (which has its own partial index behind it), omitted = everything. `filter.search` spans the
 * ticket id, subject, customer email, order number — both the submitted one and the verifier's
 * correction — and ship-to name: the things a staffer actually has in hand when someone asks
 * about a return.
 */
export const DEVICE_RMA_REQUESTS = gql`
  query deviceRmaRequests($filter: DeviceRmaFilterInput, $pagination: PaginationInput) {
    deviceRmaRequests(filter: $filter, pagination: $pagination) {
      items { ${RMA_FIELDS} }
      total
    }
  }
`;

/**
 * Stamp — or clear — the eligibility verification on one request.
 *
 * No operator identity is sent: the server takes it from the authenticated caller, so the record
 * of who stood behind a check cannot be dressed up by whoever fills in the form. Returns the whole
 * row so the table can re-render from the server's answer rather than guessing at it.
 *
 * `deviceCount` is the VERIFIER's corrected quantity and lands in its own column — it never
 * overwrites what the customer submitted, which HubSpot owns and the poll rewrites. Omitting it
 * leaves the stored correction alone; sending `0` clears it.
 */
export const SET_DEVICE_RMA_VERIFIED = gql`
  mutation setDeviceRmaVerified(
    $submissionId: String!
    $verified: Boolean!
    $previousOrderNumber: String
    $troubleshootingNotes: String
    $deviceCount: Int
    $orderDate: String
  ) {
    setDeviceRmaVerified(
      submissionId: $submissionId
      verified: $verified
      previousOrderNumber: $previousOrderNumber
      troubleshootingNotes: $troubleshootingNotes
      deviceCount: $deviceCount
      orderDate: $orderDate
    ) {
      ${RMA_FIELDS}
    }
  }
`;

/**
 * Correct — or clear — the shipping address a replacement ships to.
 *
 * Its own mutation rather than more arguments on the verification stamp: an address is fixed when
 * the customer says they have moved, which is a different moment and often a different person from
 * the eligibility decision, and must not require re-stamping it.
 *
 * Omitting `address` CLEARS the correction and the customer's own address applies again. A partial
 * address cannot be expressed — name/line1/city/zip/country are non-null on the input type.
 */
export const SET_DEVICE_RMA_SHIPPING_ADDRESS = gql`
  mutation setDeviceRmaShippingAddress($submissionId: String!, $address: DeviceRmaShipToInput) {
    setDeviceRmaShippingAddress(submissionId: $submissionId, address: $address) {
      ${RMA_FIELDS}
    }
  }
`;

/**
 * Record a replacement raised BY HAND in Shopify, and/or its tracking number.
 *
 * The manual twin of the create mutation — for an order raised in the admin before this feature
 * existed, during the weeks the app lacked `write_orders`, or for a case the button cannot express.
 * PARTIAL: omit a field to leave it alone, send '' to clear it, so tracking can be added days after
 * the order without re-entering it.
 *
 * The number is resolved against Shopify and must match EXACTLY before anything is stored.
 */
export const SET_DEVICE_RMA_REPLACEMENT_ORDER = gql`
  mutation setDeviceRmaReplacementOrder(
    $submissionId: String!
    $orderNumber: String
    $trackingNumber: String
    $trackingUrl: String
  ) {
    setDeviceRmaReplacementOrder(
      submissionId: $submissionId
      orderNumber: $orderNumber
      trackingNumber: $trackingNumber
      trackingUrl: $trackingUrl
    ) {
      ${RMA_FIELDS}
    }
  }
`;

/**
 * Release the replacement: create the Shopify order for a VERIFIED RMA.
 *
 * ONE ARGUMENT, and that is the design. The SKU comes from the device model, the quantity from
 * what the customer submitted, the address from the form — none of them travel from the browser,
 * because a typo on this path ships the wrong device or the right one to the wrong place. The
 * server refuses unless the row is verified, and refuses again if an order already exists.
 *
 * Returns the whole row, so the dialog and the table redraw from the server's answer — including
 * `replacementOrderUrl`, the admin link the browser must not build for itself.
 */
export const CREATE_DEVICE_RMA_REPLACEMENT_ORDER = gql`
  mutation createDeviceRmaReplacementOrder($submissionId: String!) {
    createDeviceRmaReplacementOrder(submissionId: $submissionId) {
      ${RMA_FIELDS}
    }
  }
`;

/** Poll HubSpot now rather than waiting up to five minutes. Idempotent; returns rows upserted. */
export const SYNC_DEVICE_RMA_REQUESTS = gql`
  mutation syncDeviceRmaRequests {
    syncDeviceRmaRequests
  }
`;

/**
 * Record — or clear — receipt of the returned unit. `condition` null clears it.
 *
 * A separate mutation from the verification stamp on purpose: different event, different person,
 * weeks apart. No operator identity is sent; the server takes it from the caller.
 */
export const SET_DEVICE_RMA_RETURN_RECEIVED = gql`
  mutation setDeviceRmaReturnReceived(
    $submissionId: String!
    $condition: String
    $missingItems: String
    $testedOutcome: String
    $testNotes: String
  ) {
    setDeviceRmaReturnReceived(
      submissionId: $submissionId
      condition: $condition
      missingItems: $missingItems
      testedOutcome: $testedOutcome
      testNotes: $testNotes
    ) {
      ${RMA_FIELDS}
    }
  }
`;

/** Resolve `cus_…` so a human confirms a name and email before anything is charged. */
export const RMA_STRIPE_CUSTOMER = gql`
  query rmaStripeCustomer($stripeCustomerId: String!) {
    rmaStripeCustomer(stripeCustomerId: $stripeCustomerId) {
      id
      name
      email
      deleted
    }
  }
`;

/**
 * Raise the restocking fee as a Stripe DRAFT invoice.
 *
 * NOTE WHAT IS NOT SENT: the amount. It is resolved server-side from the device model, so a stale
 * or tampered client cannot decide what a customer is billed. The comment is required — on a path
 * with no approver it is the whole record of why the charge exists.
 */
export const CREATE_DEVICE_RMA_RESTOCKING_INVOICE = gql`
  mutation createDeviceRmaRestockingInvoice(
    $submissionId: String!
    $stripeCustomerId: String!
    $comment: String!
  ) {
    createDeviceRmaRestockingInvoice(
      submissionId: $submissionId
      stripeCustomerId: $stripeCustomerId
      comment: $comment
    ) {
      ${RMA_FIELDS}
    }
  }
`;

/**
 * Resolve an order number to its DATE, for the warranty panel.
 *
 * LAZY at the call site, never on render — it is a live Shopify read, and firing one per keystroke
 * while an operator types an order number would be wasteful and noisy in Shopify's logs (the same
 * rule `rmaStripeCustomer` follows). `found: false` is the ordinary answer for an Amazon purchase.
 */
export const RMA_ORDER_LOOKUP = gql`
  query rmaOrderLookup($orderNumber: String!) {
    rmaOrderLookup(orderNumber: $orderNumber) {
      found
      orderName
      orderDate
      customerEmail
      orderUrl
    }
  }
`;

/**
 * Take a request out of the working queue — or put it back (omit `reason`).
 *
 * NOT a delete, and it cannot be: the table is a projection of HubSpot submissions, so deleting the
 * newest row would see it re-ingested within five minutes. A dismissed row is also NOT WORK — every
 * other mutation refuses on it — which is why this is a state rather than a list filter.
 */
export const SET_DEVICE_RMA_DISMISSED = gql`
  mutation setDeviceRmaDismissed(
    $submissionId: String!
    $reason: String
    $duplicateOfRmaNumber: Int
  ) {
    setDeviceRmaDismissed(
      submissionId: $submissionId
      reason: $reason
      duplicateOfRmaNumber: $duplicateOfRmaNumber
    ) {
      ${RMA_FIELDS}
    }
  }
`;
